import { useEffect, useRef, useState } from "react";

// Mémoire partagée des filtres KPI (DR, secteur, corners, période, dates personnalisées) entre pages et onglets.
const LS = "orbit_kpi_shared_filters";
const EVT = "orbit:kpi-filters";
export const SHARED_KEYS = ["regions", "drs", "bms", "secteurs", "enseignes", "corners", "annees", "mois", "exercice", "semaines", "year_mode", "date_from", "date_to"];
const LIST_KEYS = new Set(["regions", "drs", "bms", "secteurs", "enseignes", "corners", "annees", "mois", "semaines"]);
const FIRST_WORD_KEYS = new Set(["drs", "bms"]);

const norm = (v) => String(v ?? "").normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toUpperCase().replace(/[^A-Z0-9]/g, "");
const firstWord = (v) => norm(String(v ?? "").trim().split(/\s+/)[0]);

export const loadSharedFilters = () => { try { return JSON.parse(localStorage.getItem(LS)) || {}; } catch { return {}; } };
export const saveSharedFilters = (partial) => {
  const next = { ...loadSharedFilters() };
  Object.entries(partial).forEach(([k, v]) => { if (SHARED_KEYS.includes(k)) next[k] = v; });
  try { localStorage.setItem(LS, JSON.stringify(next)); } catch { /* ignore */ }
  window.dispatchEvent(new CustomEvent(EVT));
};
export const clearSharedFilters = () => { try { localStorage.removeItem(LS); } catch { /* ignore */ } window.dispatchEvent(new CustomEvent(EVT)); };

// Options d'une page à partir de son meta (formes hétérogènes selon les modules)
const optionsOf = (meta, key) => {
  if (!meta) return null;
  const pick = (...ks) => ks.map(k => meta[k]).find(v => Array.isArray(v) && v.length);
  if (key === "corners") { const l = pick("corners", "magasins"); return l ? l.map(c => (typeof c === "string" ? c : c.corner_key || c.key || c.corner)).filter(Boolean) : null; }
  const l = pick(key, key.replace(/s$/, ""), key === "annees" ? "years" : key === "exercice" ? "exercices" : key);
  return l ? l.map(o => (typeof o === "object" && o ? (o.value ?? o.key ?? o.v) : o)) : null;
};

// Aligne une valeur mémorisée sur les options de la page (accents/casse/prénom, S01 ↔ 1)
const matchOne = (key, v, opts) => {
  if (opts.includes(v)) return v;
  const n = norm(v); let m = opts.find(o => norm(o) === n);
  if (m !== undefined) return m;
  if (FIRST_WORD_KEYS.has(key)) { const fw = firstWord(v); m = opts.find(o => firstWord(o) === fw); if (m !== undefined) return m; }
  if (key === "semaines" || key === "annees") { const num = parseInt(n.replace(/^S/, ""), 10); m = opts.find(o => parseInt(norm(o).replace(/^S/, ""), 10) === num); if (m !== undefined) return m; }
  return undefined;
};

const reconcile = (key, value, meta) => {
  const opts = optionsOf(meta, key);
  if (!opts) return value;
  if (Array.isArray(value)) return value.map(v => matchOne(key, v, opts)).filter(v => v !== undefined);
  if (value == null || value === "") return value;
  const m = matchOne(key, value, opts);
  return m === undefined ? (LIST_KEYS.has(key) ? [] : null) : m;
};

const toPage = (shared, { map, single, meta }) => {
  const out = {};
  Object.entries(map).forEach(([pageKey, sharedKey]) => {
    if (!(sharedKey in shared)) return;
    let v = reconcile(sharedKey, shared[sharedKey], meta);
    if (single) v = Array.isArray(v) ? (v[0] ?? "") : (v ?? "");
    else if (LIST_KEYS.has(sharedKey)) v = Array.isArray(v) ? v : (v ? [v] : []);
    out[pageKey] = v;
  });
  return out;
};

const toShared = (f, { map, single, seen }) => {
  const out = {};
  Object.entries(map).forEach(([pageKey, sharedKey]) => {
    let v = pageKey in f ? f[pageKey] : undefined;
    if (v === undefined) { if (seen?.has(pageKey)) out[sharedKey] = LIST_KEYS.has(sharedKey) ? [] : null; return; }  // reset ({}) → vide partout
    if (single) v = LIST_KEYS.has(sharedKey) ? (v ? [v] : []) : (v || null);
    out[sharedKey] = v;
  });
  return out;
};

const DEFAULT_MAP = Object.fromEntries(SHARED_KEYS.map(k => [k, k]));

/**
 * useSharedKpiFilters(init, { meta, map, single, pageKey })
 * - meta    : options de la page (drs, secteurs, corners…) pour aligner les valeurs mémorisées
 * - map     : { cléPage: cléPartagée } si la page n'utilise pas les noms standard
 * - single  : la page stocke des chaînes (sélection unique) au lieu de tableaux
 * - pageKey : localStorage propre à la page pour ses filtres non partagés
 */
export function useSharedKpiFilters(init, { meta = null, map = DEFAULT_MAP, single = false, pageKey = null } = {}) {
  const cfg = useRef({ map, single });
  const [f, setF] = useState(() => {
    let base = typeof init === "function" ? init() : { ...init };
    if (pageKey) { try { base = { ...base, ...(JSON.parse(localStorage.getItem(pageKey)) || {}) }; } catch { /* ignore */ } }
    return { ...base, ...toPage(loadSharedFilters(), { ...cfg.current, meta: null }) };
  });
  const reconciled = useRef(false);
  useEffect(() => {
    if (reconciled.current || !meta) return;
    reconciled.current = true;
    setF(prev => ({ ...prev, ...toPage(toShared(prev, cfg.current), { ...cfg.current, meta }) }));
  }, [meta]);
  const skipFirst = useRef(true);
  const seen = useRef(new Set(Object.keys(f)));
  useEffect(() => {
    Object.keys(f).forEach(k => seen.current.add(k));
    if (skipFirst.current) { skipFirst.current = false; return; }
    saveSharedFilters(toShared(f, { ...cfg.current, seen: seen.current }));
    if (pageKey) { try { localStorage.setItem(pageKey, JSON.stringify(f)); } catch { /* ignore */ } }
  }, [f, pageKey]);
  return [f, setF];
}
